import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {logoutAction} from '../rootAction';
import {SelectedClientState, setClientData} from './selectedClientSlice';
import {IProjectDetails} from '../../types/project-type/projectDetailsType';
import {IDataSet} from '../../components/pages/WelcomeScreenSingleClient';

interface ReportsPageState {
  projectId?: string | null;
  workspaceId?: string | null;
  mediaEvidenceId: string | null;
  mediaEvidenceName: string | null;
  clientId?: string | null;
  selectedProjectName?: string | null;
  selectedWorkSpaceName?: string | null;
  previousReportTypeTab?: string | null;
  currentReportTypeTab?: string | null;
  workspaceDataSetName?: string | null;
  worksapceDataSetMediaId?: string | null;
  workspaceDataSetCustodianId?: number | null;
  refreshDates: RefreshDates;
  processingTab?: number | null;
  currentProject?: IProjectDetails | null;
  projectCode?: string | null;
  projectAlphaCode?: string | null;
  nlpSearchEnabled?: boolean;
  overviewDataset: IDataSet | null;
  pageOpenFrom?: string | null;
}

export interface RefreshDates {
  intakeAudit?: string | null;
  hostingUserSummary?: string | null;
  invoice?: string | null;
  processingException?: string | null;
  hostingVolume?: string | null;
  highlevelProductionMetrics?: string | null;
  processingSummary?: string | null;
  completedMappedData?: string | null;
}

interface ProjectWorkSpaceActionPayloadType {
  projectId: string;
  workspaceId: string;
  projectName: string;
  workspaceName: string;
  projectCode?: string;
  projectAlphaCode?: string;
}

interface WorkspaceDataSetActionPayloadType
  extends ProjectWorkSpaceActionPayloadType {
  workspaceDataSetName: string;
  worksapceDataSetMediaId: string;
  wokrspaceDataSetCustodianId?: number | null;
}

interface MediaEvidenceSourceActionPayloadType
  extends ProjectWorkSpaceActionPayloadType {
  mediaId: string;
  mediaName: string;
}

const initialState: ReportsPageState = {
  projectId: null,
  workspaceId: null,
  selectedProjectName: '',
  selectedWorkSpaceName: '',
  previousReportTypeTab: null,
  currentReportTypeTab: null,
  mediaEvidenceId: null,
  mediaEvidenceName: null,
  workspaceDataSetName: null,
  worksapceDataSetMediaId: null,
  workspaceDataSetCustodianId: null,
  refreshDates: {},
  processingTab: null,
  currentProject: null,
  projectCode: '',
  projectAlphaCode: '',
  nlpSearchEnabled: false,
  overviewDataset: null,
  pageOpenFrom: null,
};

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    storeSelectedDataSet: (
      state,
      action: PayloadAction<WorkspaceDataSetActionPayloadType>
    ) => {
      const {
        projectId,
        workspaceId,
        projectName,
        workspaceName,
        worksapceDataSetMediaId,
        workspaceDataSetName,
        projectAlphaCode,
        wokrspaceDataSetCustodianId,
      } = action.payload;

      executeCleanUpOnProjectWorkspaceChange(state, projectId, workspaceId);
      state.projectId = action.payload?.projectId;
      state.workspaceId = action.payload?.workspaceId;
      state.selectedProjectName = projectName;
      state.selectedWorkSpaceName = workspaceName;
      state.workspaceDataSetName = workspaceDataSetName;
      state.worksapceDataSetMediaId = worksapceDataSetMediaId;
      state.projectAlphaCode = projectAlphaCode;
      state.workspaceDataSetCustodianId = wokrspaceDataSetCustodianId ?? null;
    },
    storeProjectNWorkSpaceId: (
      state,
      action: PayloadAction<ProjectWorkSpaceActionPayloadType>
    ) => {
      const {
        projectId,
        workspaceId,
        projectName,
        workspaceName,
        projectCode,
        projectAlphaCode,
      } = action.payload;

      executeCleanUpOnProjectWorkspaceChange(state, projectId, workspaceId);

      state.projectId = projectId;
      state.workspaceId = workspaceId;
      state.selectedProjectName = projectName;
      state.selectedWorkSpaceName = workspaceName;
      state.projectCode = projectCode;
      state.projectAlphaCode = projectAlphaCode;
    },
    setMediaEvidenceId: (
      state,
      action: PayloadAction<MediaEvidenceSourceActionPayloadType>
    ) => {
      const {projectId, workspaceId} = action.payload;
      executeCleanUpOnProjectWorkspaceChange(state, projectId, workspaceId);
      state.mediaEvidenceId = action.payload.mediaId;
      state.mediaEvidenceName = action.payload.mediaName;
      state.projectId = projectId;
      state.selectedProjectName = action.payload.projectName;
      state.selectedWorkSpaceName = action.payload.workspaceName;
      state.workspaceId = workspaceId;
    },
    setProjectName: (
      state,
      action: PayloadAction<{projectId: string; projectName: string}>
    ) => {
      const {projectId, projectName} = action.payload;
     
      state.projectId = projectId;
      state.selectedProjectName = projectName;
      
    },
    setWorkspaceDataSet: (
      state,
      action: PayloadAction<{
        workspaceDataSetName: string;
        worksapceDataSetMediaId: string;
        datasetCustodianId?: number | null;
      }>
    ) => {
      const {
        worksapceDataSetMediaId,
        workspaceDataSetName,
        datasetCustodianId,
      } = action.payload;
      state.worksapceDataSetMediaId = worksapceDataSetMediaId;
      state.workspaceDataSetName = workspaceDataSetName;
      state.workspaceDataSetCustodianId = datasetCustodianId;
    },
    // This action specially define and used for evidence source list.
    setWorkspaceDataSetFromEvidenceGrid: (
      state,
      action: PayloadAction<{
        workspaceDataSetName: string;
        worksapceDataSetMediaId: string;
        workspaceDatasetCustodianId: number | null;
      }>
    ) => {
      const {
        worksapceDataSetMediaId,
        workspaceDataSetName,
        workspaceDatasetCustodianId,
      } = action.payload;
      state.worksapceDataSetMediaId = worksapceDataSetMediaId;
      state.workspaceDataSetName = workspaceDataSetName;
      state.workspaceDataSetCustodianId = workspaceDatasetCustodianId;
        // Cleanup of any previous selected media evidenceID
        state.mediaEvidenceId = null;
      state.mediaEvidenceName = null;
    },
    // This action specially define and used for evidence source list.
    setMediaIDFromEvidenceGrid: (
      state,
      action: PayloadAction<{mediaId: string}>
    ) => {
      state.mediaEvidenceId = action.payload.mediaId;
      state.mediaEvidenceName = `SAFEID${action.payload.mediaId.padStart(5, '0')}`;
      // Cleanup of any previous selected dataset
      state.worksapceDataSetMediaId = null;
      state.workspaceDataSetName = null;
    },
    clearWorkspaceDataSet: (state) => {
      state.worksapceDataSetMediaId = null;
      state.workspaceDataSetName = null;
    },
    setPreviousReportTypeTab: (
      state,
      action: PayloadAction<string | null | undefined>
    ) => {
      state.previousReportTypeTab = action?.payload;
    },
    setRefreshDates: (state, action: PayloadAction<RefreshDates>) => {
      state.refreshDates = action?.payload;
    },
    setCurrentReportTypeTab: (
      state,
      action: PayloadAction<string | null | undefined>
    ) => {
      state.currentReportTypeTab = action?.payload;
    },
    setProcessingTab: (
      state,
      action: PayloadAction<number | null | undefined>
    ) => {
      state.processingTab = action?.payload;
    },
    setCurrentProject: (
      state,
      action: PayloadAction<IProjectDetails | null | undefined>
    ) => {
      state.currentProject = action?.payload;
    },
    setProjectNlpSearch(state, action: PayloadAction<boolean>) {
      state.nlpSearchEnabled = action.payload;
    },
    setOverviewDataset(state, action: PayloadAction<IDataSet>) {
      state.overviewDataset = action.payload;
    },
    setReportPageOpenFrom(state, action: PayloadAction<string | null>) {
      state.pageOpenFrom = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutAction, () => {
      return initialState;
    });
    /**
     * This action is added here to get client select event here so
     * this slice always in sync with selectedClient Slice state for clientId.
     * Storing clientId here to identify when selected client got change then
     * perform the required action here to clear the report state
     */
    builder.addCase(
      setClientData,
      (state, action: PayloadAction<SelectedClientState>) => {
        if (state.clientId !== action.payload.clientId) {
          // Handle Selected client change use here like reset the state with initial state
          state.projectId = null;
          state.workspaceId = null;
          state.mediaEvidenceId = null;
          state.mediaEvidenceName = null;
          state.selectedWorkSpaceName = null;
          state.selectedProjectName = null;
          state.worksapceDataSetMediaId = null;
          state.workspaceDataSetName = null;
        }
        state.clientId = action.payload.clientId;
      }
    );
  },
});

const executeCleanUpOnProjectWorkspaceChange = (
  state: ReportsPageState,
  projectId?: string,
  workspaceId?: string
) => {
  if (state.projectId && projectId && projectId !== state.projectId) {
    /**
     * Clear processing summary because when selected project got changed then
     * current selected dataset for porcessing summary is no longer part of current selected
     * project
     */
    state.mediaEvidenceId = null;
    state.mediaEvidenceName = null;
    state.worksapceDataSetMediaId = null;
    state.workspaceDataSetName = null;
  } else if (
    state.workspaceId &&
    workspaceId &&
    workspaceId !== state.workspaceId
  ) {
    /**
     * Clear processing summary because when selected workspace got changed then
     * current selected dataset for porcessing summary is no longer part of current selected workspace
     */
    state.mediaEvidenceId = null;
    state.mediaEvidenceName = null;
    state.worksapceDataSetMediaId = null;
    state.workspaceDataSetName = null;
  }
};

export const {
  storeSelectedDataSet,
  storeProjectNWorkSpaceId,
  setPreviousReportTypeTab,
  setCurrentReportTypeTab,
  setMediaEvidenceId,
  setWorkspaceDataSet,
  setRefreshDates,
  setProcessingTab,
  setCurrentProject,
  setProjectName,
  setProjectNlpSearch,
  setOverviewDataset,
  clearWorkspaceDataSet,
  setMediaIDFromEvidenceGrid,
  setWorkspaceDataSetFromEvidenceGrid,
  setReportPageOpenFrom,
} = reportsSlice.actions;

export default reportsSlice.reducer;
